/**
 * 市民端唯一的请求出口。
 *
 * 三条纪律：
 *  ① **默认走真接口**。后端没起就显示明确的错误态（见 ErrorState），
 *     **不做 mock 回落**——市民端回落等于把编的数据当成协会的答复，一次都不许。
 *  ② 接口基址：显式的 `VITE_API_BASE` 优先（空串＝同源）；**没配时按运行时所在域判断**——
 *     页面开在 localhost/127.0.0.1 上就连 `http://localhost:3001`（本机开发），
 *     开在任何其他域名/IP 上就走**同源** `/api/...`（由 nginx 反代到后端）。
 *     ★ 2026-08-27 线上事故：早先这里写死缺省 `http://localhost:3001`，而构建时
 *       从未设过 `VITE_API_BASE` —— 于是发到服务器的包让**每个访客去连自己电脑的 3001**，
 *       市民端全线「连不上服务端」。之所以一直没暴露，是此前实测都在本机做的。
 *       教训：**构建期开关一旦漏配就会静默产出错误产物**，能在运行时判断的就别赖构建期。
 *  ③ 协会数据一律走后端 `/api/citizen/credit/*` 代理。协会接口的应用标识、密钥、
 *     乐享令牌一律留在服务端，前端源码与构建产物里连字样都不许出现
 *     （验收会 grep 产物；连注释都要避开那几个词，sourcemap 会把注释一起打进去）。
 */

import type { CitizenEnvelope } from './contract';
import {
  writeTimeoutMs,
  writeTimeoutText,
  type AttachmentSizeLike,
  type WriteCheckWhere,
} from './write-timeout';

/** 本机开发域：只有这几个才连 localhost:3001，其余一律同源 */
function isLocalHost(host: string): boolean {
  return host === 'localhost' || host === '127.0.0.1' || host === '::1' || host === '[::1]';
}

function resolveBase(): string {
  const configured = import.meta.env['VITE_API_BASE'] as string | undefined;
  // 显式配置最优先——包括显式配成空串（同源）
  if (typeof configured === 'string') return configured;
  // 没配：按页面自己开在哪儿判断。开在服务器上就同源，不去连访客的电脑。
  if (typeof location !== 'undefined' && !isLocalHost(location.hostname)) return '';
  return 'http://localhost:3001';
}

const RAW_BASE = resolveBase();
/** 末尾斜杠去掉，免得拼出 `//api` */
export const API_BASE = RAW_BASE.replace(/\/+$/, '');

/**
 * 全站默认超时。
 *
 * ★★ **不许为了带附件的那几条写接口把它调大**（2026-09-04 立）。
 *   它管的是列表、详情、区划表这类**读**接口：读接口就该快，慢了要早点告诉人。
 *   写接口那一套时长在 `./write-timeout.ts`，按附件体积单独算，与本值井水不犯河水。
 */
const TIMEOUT_MS = 8000;

/**
 * **读**接口超时时给人看的那句话。
 *
 * 它敢说「重试」，是因为读一次不产生任何后果。
 * 写接口**绝不能**用这一句 —— 理由见 `./write-timeout.ts` 文件头那次重复提交事故。
 */
export const READ_TIMEOUT_TEXT = '请求超时，请稍后重试';

/**
 * 会话令牌存 **localStorage**（2026-09-06 改，原来是 sessionStorage）。
 *
 * ★ 主公把市民端会话定为 30 天。而 `sessionStorage` 关掉页面就清空 ——
 *   在微信里退出一次公众号、或者切走再回来，令牌就没了，
 *   服务端存 30 天毫无意义。前后两处必须一致，改一处等于没改。
 *
 * ★ 手机是私人设备，令牌长期驻留的风险远小于「每次点进来都要重新收短信」
 *   对市民造成的阻力。后台那一侧同样改了，但那边共用设备的风险更实在，
 *   配套动作是「离开时点退出登录」，已列进上线清单。
 */
const TOKEN_KEY = 'cdfjx-citizen-token';
const PHONE_KEY = 'cdfjx-citizen-phone-masked';

export function getToken(): string {
  try {
    return localStorage.getItem(TOKEN_KEY) ?? '';
  } catch {
    return '';
  }
}

export function getPhoneMasked(): string {
  try {
    return localStorage.getItem(PHONE_KEY) ?? '';
  } catch {
    return '';
  }
}

export function setSession(token: string, phoneMasked: string): void {
  try {
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(PHONE_KEY, phoneMasked);
  } catch {
    /* 隐私模式下写不进去就算了，本次会话内存里也没别的地方可放 */
  }
}

export function clearSession(): void {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(PHONE_KEY);
  } catch {
    /* 同上 */
  }
}

/* ────────────────────────────────────────────────────────────────────────
 * ★ 2026-08-27 晚 · 主公改口径：**一次登录，按号码找** ★
 * ────────────────────────────────────────────────────────────────────────
 *
 * 本文件此处**曾经有第二把会话**（`cdfjx-respondent-token` ＋ 另一个打码号键），
 * 给「被投诉方」那条线单用。现已整块删除，理由是主公的原话：
 *   「同一个手机号既有投诉也有被投诉，他登录了，就按照号码找就好了。」
 *
 * 所以整个市民端**只有上面那一把 `cdfjx-citizen-token`**：
 *   · 一个登录页、一把路由守卫、一个鉴权头 `x-citizen-token`；
 *   · 登录之后手里是两个按同一个号码筛出来的列表 ——
 *     「我的投诉」（他发起的）与「收到的投诉」（投诉里把这个号码填成联系电话的）。
 * 系统里**不再存在"被投诉方身份"这个东西**，因此也不该再有第二把令牌可存。
 *
 * ⚠ 谁要在这里再加一把键，先回来读这一段：两把键当初的理由（两条身份同时在线）
 *   已经随口径作废 —— 只有一条身份，就是这个手机号。
 */

export class ApiError extends Error {
  /** HTTP 状态码；0 表示压根没连上（网络错 / 超时） */
  readonly status: number;
  /** true = 登录态失效，调用方应把用户请回验证码那一步 */
  readonly unauthorized: boolean;
  /**
   * **结果未知**：写请求超时了，服务端可能已经把这一笔办成了。
   *
   * ★ 有它的时候，界面上**一个「失败」字都不许出现**——标题也不行。
   *   标题写「提交失败」而正文写「可能已经成功」，是自相矛盾，
   *   而人只看得进标题。判据放在错误对象上，页面 `if` 一下即可，
   *   不必也不许各自猜（`isInconclusive()` 就是给页面用的那一下）。
   */
  readonly inconclusive: boolean;

  constructor(message: string, status: number, inconclusive = false) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.unauthorized = status === 401 || status === 403;
    this.inconclusive = inconclusive;
  }
}

/** 这一次的错误是不是「结果未知」（写请求超时）。页面据此挑标题，别自己解析文案。 */
export function isInconclusive(e: unknown): boolean {
  return e instanceof ApiError && e.inconclusive;
}

function authHeaders(): Record<string, string> {
  const token = getToken();
  // 契约定死的请求头名：x-citizen-token
  return token ? { 'x-citizen-token': token } : {};
}

export interface RawRequestInit {
  method?: 'GET' | 'POST';
  /** 会被 JSON.stringify；`undefined` 表示不带体 */
  body?: unknown;
  /** 附加请求头。同名键覆盖默认值（含鉴权头） */
  headers?: Record<string, string>;
  timeoutMs?: number;
  /**
   * **写操作专用**：这一笔超时之后，让市民去哪儿核对结果。
   *
   * ★ 给了它，超时话术就从「请求超时，请稍后重试」换成「可能已经提交成功，请先去…核对」。
   *   判据落在这里、由 api 层的每条写接口**声明一次**，页面不参与 ——
   *   页面一旦要自己选话术，三个页面就会有三种口径，而这正是 2026-09-04 那次
   *   「界面说失败、其实成了、用户提了三遍」事故的形状。
   * ★ 取值只能来自 `./write-timeout.ts` 的 `WRITE_CHECK_WHERE`（类型已经卡死），
   *   本文件不写任何去处字符串。
   */
  writeCheck?: WriteCheckWhere;
}

/**
 * **全站唯一的 fetch 出口**：超时、网络错、HTTP 状态、JSON 解析都在这里一处处理，
 * 返回的是**解析后的响应体原样**（不拆信封）。
 *
 * 抽出这一层是为了让「收到的投诉」那几条接口（出参外层不是市民端信封）
 * 共用同一套超时与错误措辞——另写一份 fetch 就会分叉出第二种超时时长、第二种报错话术。
 */
export async function requestRaw(path: string, init: RawRequestInit = {}): Promise<unknown> {
  const timeoutMs = init.timeoutMs ?? TIMEOUT_MS;
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method: init.method ?? 'GET',
      ...(init.body === undefined ? {} : { body: JSON.stringify(init.body) }),
      signal: ctrl.signal,
      headers: {
        'Content-Type': 'application/json',
        // 全站唯一的鉴权头。**没有第二把令牌可带**（见上面那段口径说明），
        // 所以这里也不再留「不带令牌」的开关 —— 有令牌就带，没有就不带。
        ...authHeaders(),
        ...(init.headers ?? {}),
      },
    });
  } catch (e) {
    clearTimeout(timer);
    // ★ **不把接口基址与路径拼进给市民看的那句话**（2026-08-26 修）。
    //   这条消息会被 `errText()` 原样弹到市民脸上，原先长这样：
    //     「请求超时（http://localhost:3001/api/citizen/credit/search?name=%E8%B5%96…）」
    //   —— 排障信息不该给市民看，这条纪律 States.tsx 早就立过，
    //   偏偏这个出口漏了，于是每一次超时都在破例。路径在服务端日志里查得到。
    //
    // ★ 2026-09-04：超时这一支**分两种**。
    //   读接口照旧说「请求超时，请稍后重试」；**写**接口（`init.writeCheck` 有值）
    //   一律不许断言失败 —— 连接断了只说明前端没等到回话，不说明服务端没干成。
    //   当天主公就是照着旧话术重试了三次，服务端三次全成，落了三条重复答辩。
    const timedOut = e instanceof Error && e.name === 'AbortError';
    const inconclusive = timedOut && init.writeCheck !== undefined;
    const reason = timedOut
      ? inconclusive
        ? writeTimeoutText(init.writeCheck as WriteCheckWhere)
        : READ_TIMEOUT_TEXT
      : '连不上服务端';
    throw new ApiError(reason, 0, inconclusive);
  } finally {
    clearTimeout(timer);
  }

  const text = await res.text();
  let parsed: unknown = undefined;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      /* 不是 JSON，下面按状态码报 */
    }
  }

  if (!res.ok) {
    throw new ApiError(pickMessage(parsed) ?? `HTTP ${res.status}`, res.status);
  }
  if (parsed === undefined) throw new ApiError('服务端返回了空响应', res.status);
  return parsed;
}

/**
 * 发一次请求并拆信封。
 *
 * 后端约定：业务成败一律用 `{ ok, error?, data? }` 表达。
 * 但 Nest 的框架级错误（400 校验、404 路由不存在）走的是 `{ statusCode, message }`，
 * 两种都要能把中文原因拿出来给用户看，不能吞成一句「请求失败」。
 */
async function request<T>(path: string, init: RawRequestInit): Promise<T> {
  const parsed = await requestRaw(path, init);

  const env = parsed as CitizenEnvelope<T>;
  if (typeof env !== 'object' || env === null || typeof env.ok !== 'boolean') {
    // 走到这里 HTTP 一定是 2xx（非 2xx 在 requestRaw 里已经带着真状态码抛掉了），
    // 所以这里的状态码只是个占位，不参与 `unauthorized` 判定。
    throw new ApiError('服务端响应不是市民端信封格式（契约不符）', 200);
  }
  if (!env.ok) throw new ApiError(env.error || '服务端未说明失败原因', 200);
  if (env.data === undefined) throw new ApiError('服务端返回 ok 但没有 data（契约不符）', 200);
  return env.data;
}

function pickMessage(parsed: unknown): string | null {
  if (typeof parsed !== 'object' || parsed === null) return null;
  const o = parsed as Record<string, unknown>;
  if (typeof o['error'] === 'string' && o['error']) return o['error'];
  const m = o['message'];
  if (typeof m === 'string' && m) return m;
  if (Array.isArray(m) && m.length) return m.map(String).join('；');
  return null;
}

export function apiGet<T>(path: string, timeoutMs?: number): Promise<T> {
  return request<T>(path, { method: 'GET', ...(timeoutMs === undefined ? {} : { timeoutMs }) });
}

/**
 * 走协会接口的那几个查询专用的超时。
 *
 * 8 秒对自家接口够用，对**代理到协会**的查询不够：按姓名搜要按五个诚信档位各查一次，
 * 单次实测约 1.4 秒，五次串行就 7 秒起步 —— 实测「赖沙」这一搜正好卡在 8 秒线上超时，
 * 页面弹「请求超时」，看着像协会查无此人。**超时值必须比上游的真实耗时留出余量**，
 * 否则就是自己把自己判失败。串行不改成并发，是不给协会侧添压（对端承压能力不能假设）。
 */
export const CREDIT_TIMEOUT_MS = 25000;

/**
 * `timeoutMs` 是为**提交前要素预填**加的（2026-08-27）：
 * 那条接口自己在服务端就卡着 `PREFILL_TIMEOUT_MS`(8s) 到点空手返回，
 * 前端这一侧若也用默认的 8 秒，两个 8 秒会赛跑 —— 谁先到不一定，
 * 于是「服务端如实告诉你 timeout」这条信息有一半概率被前端的 AbortError 吃掉，
 * 界面上就分不清「跑了没成」与「压根没连上」。前端给宽一点，让服务端那道先说话。
 */
export function apiPost<T>(path: string, body: unknown, timeoutMs?: number): Promise<T> {
  return request<T>(path, { method: 'POST', body, ...(timeoutMs === undefined ? {} : { timeoutMs }) });
}

/* ────────────────────────────────────────────────────────────────────────
 * 带附件的写接口：时长与话术**捆在一起**发
 * ──────────────────────────────────────────────────────────────────────── */

/**
 * 一条写接口要的那**一整套**：按附件体积算出的超时时长 ＋ 超时该说的话。
 *
 * ★★ 捆在一个函数里，是因为这两半**只取一半就是错的**：
 *   · 只加长时长、话术照旧 → 慢网下照样弹「请求超时，请稍后重试」，人照旧去重提；
 *   · 只改话术、时长照旧 8 秒 → 每一次带附件的提交都必然走到那句话上，
 *     等于把「例外提示」变成了「常规提示」，说了等于没说。
 *   2026-09-04 那次事故两半都缺。所以这里不提供「只要一半」的用法。
 *
 * `attachments` 直接传**要发出去的那个数组本身**（`证据` / `attachments`），
 * 体积在 `write-timeout.ts` 里算，调用点不碰秒数。
 */
export function writeRequestInit(
  attachments: readonly AttachmentSizeLike[] | undefined,
  where: WriteCheckWhere,
): { timeoutMs: number; writeCheck: WriteCheckWhere } {
  return { timeoutMs: writeTimeoutMs(attachments), writeCheck: where };
}

/**
 * 市民端信封的写接口（提交投诉、信息补录）。
 *
 * 「收到的投诉」那条答辩走的是 `requestRaw`（出参外层不是市民端信封），
 * 它自己拼 `writeRequestInit()`——两条路取的是**同一套数字和同一句话**。
 */
export function apiPostWrite<T>(
  path: string,
  body: unknown,
  attachments: readonly AttachmentSizeLike[] | undefined,
  where: WriteCheckWhere,
): Promise<T> {
  return request<T>(path, { method: 'POST', body, ...writeRequestInit(attachments, where) });
}

/** 拼查询串，空值不进 URL。 */
export function qs(params: Record<string, string | number | undefined | null>): string {
  const p = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null || v === '') return;
    p.set(k, String(v));
  });
  const s = p.toString();
  return s ? `?${s}` : '';
}

export function errText(e: unknown): string {
  if (e instanceof ApiError) return e.message;
  if (e instanceof Error) return e.message;
  return String(e);
}
